import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth-context';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Eye, EyeOff, Shield, Lock, ArrowRight, Fingerprint, Sparkles, Globe, Zap, ChevronRight, ArrowLeft, QrCode, Copy, Check, Smartphone } from 'lucide-react';
import { oauthAPI, oidcAPI } from '../lib/api';
import { QRCodeSVG } from 'qrcode.react';

/* ─── animated particles on the brand panel ─── */
function Particles() {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {[...Array(20)].map((_, i) => (
        <div key={i} className="absolute rounded-full opacity-[0.08]" style={{
          width: `${Math.random() * 4 + 1}px`, height: `${Math.random() * 4 + 1}px`,
          background: '#B89B5E',
          left: `${Math.random() * 100}%`, top: `${Math.random() * 100}%`,
          animation: `float ${6 + Math.random() * 10}s ease-in-out infinite`,
          animationDelay: `${Math.random() * 5}s`,
        }} />
      ))}
    </div>
  );
}

/* ─── 2FA code input — plain visible input field ─── */
function OtpInput({ value, onChange, autoFocus = true }) {
  return (
    <input
      type="text"
      inputMode="numeric"
      autoComplete="one-time-code"
      maxLength={6}
      value={value}
      onChange={e => onChange(e.target.value.replace(/\D/g, '').slice(0, 6))}
      autoFocus={autoFocus}
      placeholder="000000"
      style={{
        width: '100%',
        height: 56,
        textAlign: 'center',
        fontSize: 28,
        fontWeight: 700,
        letterSpacing: 16,
        color: '#ffffff',
        backgroundColor: 'rgba(255,255,255,0.08)',
        border: '2px solid rgba(255,255,255,0.15)',
        borderRadius: 14,
        outline: 'none',
        caretColor: '#B89B5E',
        fontFamily: 'monospace',
      }}
      onFocus={e => { e.target.style.borderColor = '#B89B5E'; e.target.style.backgroundColor = 'rgba(184,155,94,0.08)'; }}
      onBlur={e => { e.target.style.borderColor = 'rgba(255,255,255,0.15)'; e.target.style.backgroundColor = 'rgba(255,255,255,0.08)'; }}
    />
  );
}

/* ─── step indicator ─── */
function StepIndicator({ current, total }) {
  return (
    <div className="flex items-center gap-2 justify-center mb-8">
      {[...Array(total)].map((_, i) => (
        <div key={i} className={`h-1 rounded-full transition-all duration-300 ${
          i < current ? 'w-8 bg-[#B89B5E]' :
          i === current ? 'w-8 bg-[#B89B5E]/60' :
          'w-4 bg-white/10'
        }`} />
      ))}
    </div>
  );
}

const ADMIN_ROLES = ['super_admin', 'admin', 'editor', 'producer', 'journalist',
  'photographer', 'designer', 'data_journalist', 'social_media_manager',
  'seo_specialist', 'ad_operations', 'subscription_manager', 'analytics_analyst',
  'hr_admin', 'finance_controller', 'legal_counsel', 'external_contributor', 'moderator'];

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [oauthProviders, setOauthProviders] = useState([]);
  const [oidcEnabled, setOidcEnabled] = useState(false);

  // MFA state  — 3 steps: 'credentials' | 'mfa_setup' | 'mfa_verify'
  const [step, setStep] = useState('credentials');
  const [mfaToken, setMfaToken] = useState('');
  const [mfaEmail, setMfaEmail] = useState('');
  const [provisioningUri, setProvisioningUri] = useState('');
  const [mfaSecret, setMfaSecret] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [mfaLoading, setMfaLoading] = useState(false);
  const [mfaCountdown, setMfaCountdown] = useState(300);
  const [secretCopied, setSecretCopied] = useState(false);

  const { login, verifyMfaLogin, completeMfaSetup } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    oauthAPI.getProviders().then(r => setOauthProviders(r.data.providers || [])).catch(() => {});
    oidcAPI.config().then(r => setOidcEnabled(r.data.enabled)).catch(() => {});
  }, []);

  // Countdown timer for MFA steps
  useEffect(() => {
    if (step !== 'mfa_verify' && step !== 'mfa_setup') return;
    setMfaCountdown(300);
    const timer = setInterval(() => {
      setMfaCountdown(prev => {
        if (prev <= 1) { clearInterval(timer); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [step]);

  const navigateAfterLogin = (user) => {
    navigate(ADMIN_ROLES.includes(user.role) ? '/admin' : '/');
  };

  /* ─── Step 1: Email + Password ─── */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await login(email, password);

      // Staff with MFA already set up → go to OTP verify
      if (result.mfa_required) {
        setMfaToken(result.mfa_token);
        setMfaEmail(result.user_email);
        setOtpCode('');
        setStep('mfa_verify');
        return;
      }

      // Staff without MFA → first-time setup with QR code
      if (result.mfa_setup_required) {
        setMfaToken(result.mfa_token);
        setMfaEmail(result.user_email);
        setProvisioningUri(result.provisioning_uri);
        setMfaSecret(result.mfa_secret);
        setOtpCode('');
        setStep('mfa_setup');
        return;
      }

      // Non-staff (reader) → direct login
      toast.success(`Welcome back, ${result.full_name}!`);
      navigateAfterLogin(result);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  /* ─── Step 2: First-time MFA setup → verify first code ─── */
  const handleSetupComplete = async (e) => {
    e?.preventDefault();
    if (otpCode.length !== 6) return;
    setMfaLoading(true);
    try {
      const user = await completeMfaSetup(mfaToken, otpCode);
      toast.success('Two-factor authentication enabled successfully!');
      navigateAfterLogin(user);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid code. Make sure you scanned the QR code correctly.');
      setOtpCode('');
    } finally {
      setMfaLoading(false);
    }
  };

  /* ─── Step 3: Subsequent login → verify OTP ─── */
  const handleMfaVerify = async (e) => {
    e?.preventDefault();
    if (otpCode.length !== 6) return;
    setMfaLoading(true);
    try {
      const user = await verifyMfaLogin(mfaToken, otpCode);
      toast.success(`Welcome back, ${user.full_name}!`);
      navigateAfterLogin(user);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid verification code');
      setOtpCode('');
    } finally {
      setMfaLoading(false);
    }
  };

  // Auto-submit OTP when 6 digits entered
  const otpSubmitRef = useRef(null);
  useEffect(() => {
    if (otpCode.length === 6) {
      clearTimeout(otpSubmitRef.current);
      otpSubmitRef.current = setTimeout(() => {
        if (step === 'mfa_setup') handleSetupComplete();
        if (step === 'mfa_verify') handleMfaVerify();
      }, 150);
    }
    return () => clearTimeout(otpSubmitRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otpCode, step]);

  const formatTime = (s) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;

  const copySecret = () => {
    navigator.clipboard.writeText(mfaSecret);
    setSecretCopied(true);
    toast.success('Secret copied to clipboard');
    setTimeout(() => setSecretCopied(false), 2000);
  };

  const goBack = () => {
    setStep('credentials');
    setOtpCode('');
    setMfaToken('');
    setProvisioningUri('');
    setMfaSecret('');
  };

  const features = [
    { icon: Shield, label: 'Enterprise-grade security', desc: 'Mandatory 2FA for all staff' },
    { icon: Sparkles, label: 'AI-powered journalism', desc: 'Intelligent content tools' },
    { icon: Globe, label: 'Global distribution', desc: 'Multi-edition publishing' },
    { icon: Zap, label: 'Real-time breaking news', desc: 'Instant push & live blogs' },
  ];

  return (
    <div className="min-h-screen flex bg-[#0A0B0E]">
      {/* ─── Left Panel ─── */}
      <div className="hidden lg:flex lg:w-[52%] relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0D0E12] via-[#121316] to-[#0A0B0E]" />
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#B89B5E]/30 to-transparent" />
        <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#B89B5E]/20 to-transparent" />
        <div className="absolute top-0 right-0 w-px h-full bg-gradient-to-b from-transparent via-[#B89B5E]/10 to-transparent" />
        <Particles />

        <div className="relative z-10 flex flex-col justify-between p-12 xl:p-16 w-full">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#B89B5E] to-[#8B7340] flex items-center justify-center shadow-lg shadow-[#B89B5E]/20">
              <span className="text-white font-bold text-lg">N</span>
            </div>
            <span className="news-masthead text-2xl font-bold text-white tracking-tight">Neural News</span>
          </div>

          <div className="flex-1 flex flex-col justify-center -mt-8">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#B89B5E]/10 border border-[#B89B5E]/20 w-fit mb-6">
              <div className="w-1.5 h-1.5 rounded-full bg-[#B89B5E] animate-pulse" />
              <span className="text-[#B89B5E] text-xs font-medium tracking-wide uppercase">Trusted by newsrooms worldwide</span>
            </div>
            <h1 className="text-4xl xl:text-5xl font-bold text-white leading-[1.15] mb-5">
              The future of<br />
              <span className="bg-gradient-to-r from-[#B89B5E] via-[#D4B96E] to-[#B89B5E] bg-clip-text text-transparent">digital journalism</span>
            </h1>
            <p className="text-white/40 text-base xl:text-lg leading-relaxed max-w-lg">
              Enterprise-grade news platform with AI-powered tools, real-time collaboration, and military-grade security.
            </p>
            <div className="mt-10 grid grid-cols-2 gap-4">
              {features.map((f, i) => (
                <div key={i} className="group flex items-start gap-3 p-3 rounded-xl border border-white/[0.04] bg-white/[0.02] hover:bg-white/[0.04] hover:border-[#B89B5E]/20 transition-all duration-300">
                  <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#B89B5E]/15 to-[#B89B5E]/5 flex items-center justify-center flex-shrink-0 group-hover:from-[#B89B5E]/25 group-hover:to-[#B89B5E]/10 transition-all">
                    <f.icon className="w-4 h-4 text-[#B89B5E]" />
                  </div>
                  <div>
                    <p className="text-white/80 text-sm font-medium leading-tight">{f.label}</p>
                    <p className="text-white/30 text-xs mt-0.5">{f.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-between text-white/20 text-xs">
            <span>&copy; 2026 Neural News Media Group</span>
            <div className="flex items-center gap-1.5">
              <Lock className="w-3 h-3" />
              <span>256-bit SSL Encrypted</span>
            </div>
          </div>
        </div>
      </div>

      {/* ─── Right Panel ─── */}
      <div className="flex-1 flex items-center justify-center px-6 py-8 relative overflow-y-auto">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-[#B89B5E]/[0.03] blur-3xl pointer-events-none" />

        <div className="w-full max-w-[420px] relative z-10">
          {/* Mobile logo */}
          <div className="flex items-center gap-2.5 lg:hidden mb-10">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#B89B5E] to-[#8B7340] flex items-center justify-center">
              <span className="text-white font-bold text-base">N</span>
            </div>
            <span className="news-masthead text-xl font-bold text-white">Neural News</span>
          </div>

          {/* ════════════════ STEP 1: CREDENTIALS ════════════════ */}
          {step === 'credentials' && (
            <div className="animate-in fade-in duration-500">
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-white mb-1.5">Welcome back</h2>
                <p className="text-white/40 text-sm">Sign in to your Neural News account</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="text-xs font-medium text-white/50 uppercase tracking-wider block mb-2">Email address</label>
                  <input
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    className="w-full rounded-xl px-4 py-3.5 bg-white/[0.06] border border-white/[0.08] text-white placeholder-white/25 focus:outline-none focus:ring-2 focus:ring-[#B89B5E]/50 focus:border-[#B89B5E]/50 text-sm transition-all duration-200 hover:bg-white/[0.08]"
                    placeholder="admin@neuralnews.com"
                    required
                    autoComplete="email"
                    data-testid="login-email-input"
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-xs font-medium text-white/50 uppercase tracking-wider">Password</label>
                    <button type="button" className="text-xs text-[#B89B5E] hover:text-[#D4B96E] transition-colors">Forgot?</button>
                  </div>
                  <div className="relative group">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      className="w-full rounded-xl px-4 py-3.5 bg-white/[0.06] border border-white/[0.08] text-white placeholder-white/25 focus:outline-none focus:ring-2 focus:ring-[#B89B5E]/50 focus:border-[#B89B5E]/50 text-sm pr-12 transition-all duration-200 hover:bg-white/[0.08]"
                      placeholder="Enter your password"
                      required
                      autoComplete="current-password"
                      data-testid="login-password-input"
                    />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60 transition-colors">
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <Button
                  type="submit" disabled={loading}
                  className="w-full h-12 rounded-xl bg-gradient-to-r from-[#B89B5E] to-[#9A8249] hover:from-[#C9AA6A] hover:to-[#A8904F] text-white font-semibold text-sm shadow-lg shadow-[#B89B5E]/20 hover:shadow-[#B89B5E]/30 transition-all duration-300 border-0 flex items-center justify-center gap-2 group"
                  data-testid="login-submit-button"
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <>Sign In<ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" /></>
                  )}
                </Button>
              </form>

              {(oauthProviders.length > 0 || oidcEnabled) && (
                <div className="mt-7">
                  <div className="flex items-center gap-3 mb-5">
                    <div className="flex-1 h-px bg-white/[0.06]" />
                    <span className="text-[11px] text-white/25 uppercase tracking-wider">or continue with</span>
                    <div className="flex-1 h-px bg-white/[0.06]" />
                  </div>
                  <div className="flex flex-col gap-2.5">
                    {oauthProviders.map(p => (
                      <a key={p.id} href={p.id === 'google' ? oauthAPI.googleUrl() : oauthAPI.githubUrl()}>
                        <Button variant="outline" className="w-full h-11 text-sm rounded-xl bg-white/[0.04] border-white/[0.08] text-white/70 hover:bg-white/[0.08] hover:text-white transition-all">
                          {p.id === 'google' ? '🔵' : '⚫'} Sign in with {p.name}
                        </Button>
                      </a>
                    ))}
                    {oidcEnabled && (
                      <a href={oidcAPI.loginUrl()}>
                        <Button variant="outline" className="w-full h-11 text-sm rounded-xl bg-white/[0.04] border-white/[0.08] text-white/70 hover:bg-white/[0.08] hover:text-white transition-all">
                          <Lock className="w-3.5 h-3.5 mr-2" /> Sign in with SSO
                        </Button>
                      </a>
                    )}
                  </div>
                </div>
              )}

              <p className="text-center text-sm text-white/30 mt-7">
                Don't have an account?{' '}
                <Link to="/register" className="text-[#B89B5E] hover:text-[#D4B96E] font-medium transition-colors">
                  Create one<ChevronRight className="w-3.5 h-3.5 inline ml-0.5" />
                </Link>
              </p>

              <div className="mt-8 p-4 rounded-xl border border-white/[0.06] bg-white/[0.02]">
                <p className="text-[11px] font-medium text-white/30 uppercase tracking-wider mb-2.5">Demo Accounts</p>
                <div className="space-y-1.5 text-xs font-mono">
                  <p className="text-white/40"><span className="text-[#B89B5E] font-semibold font-sans">Admin</span> admin@neuralnews.com / Admin123!</p>
                  <p className="text-white/40"><span className="text-[#B89B5E] font-semibold font-sans">Editor</span> editor@neuralnews.com / Editor123!</p>
                  <p className="text-white/40"><span className="text-[#B89B5E] font-semibold font-sans">Journalist</span> journalist@neuralnews.com / Journalist123!</p>
                </div>
              </div>
            </div>
          )}

          {/* ════════════════ STEP 2: FIRST-TIME MFA SETUP (QR CODE) ════════════════ */}
          {step === 'mfa_setup' && (
            <div className="animate-in fade-in slide-in-from-right-4 duration-500">
              <button onClick={goBack} className="flex items-center gap-1.5 text-white/40 hover:text-white/70 text-sm mb-4 transition-colors group">
                <ArrowLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />
                Back to sign in
              </button>

              <div className="text-center mb-4">
                <h2 className="text-lg font-bold text-white mb-1">Set Up Two-Factor Authentication</h2>
                <p className="text-white/40 text-xs">
                  2FA is required for all staff. Scan the QR code with your authenticator app.
                </p>
              </div>

              {/* QR Code */}
              <div className="flex justify-center mb-3">
                <div className="p-3 bg-white rounded-xl shadow-2xl shadow-black/40">
                  <QRCodeSVG
                    value={provisioningUri}
                    size={150}
                    level="M"
                    includeMargin={false}
                    bgColor="#FFFFFF"
                    fgColor="#121316"
                  />
                </div>
              </div>

              {/* Supported apps */}
              <div className="flex items-center justify-center gap-3 mb-3">
                <span className="text-white/30 text-[10px]">Google Authenticator</span>
                <span className="text-white/10">|</span>
                <span className="text-white/30 text-[10px]">Microsoft Authenticator</span>
                <span className="text-white/10">|</span>
                <span className="text-white/30 text-[10px]">Duo Mobile</span>
              </div>

              {/* Manual secret key */}
              <div className="mb-4">
                <p className="text-white/30 text-[10px] uppercase tracking-wider text-center mb-1.5">Or enter this key manually</p>
                <div className="flex items-center gap-2 justify-center">
                  <code className="px-2.5 py-1.5 bg-white/[0.06] border border-white/[0.08] rounded-lg text-[#B89B5E] text-xs font-mono tracking-wider select-all">
                    {mfaSecret}
                  </code>
                  <button onClick={copySecret} className="p-1.5 rounded-lg bg-white/[0.06] border border-white/[0.08] hover:bg-white/[0.1] transition-colors text-white/50 hover:text-white/80">
                    {secretCopied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                  </button>
                </div>
              </div>

              {/* Divider */}
              <div className="flex items-center gap-3 mb-4">
                <div className="flex-1 h-px bg-white/[0.06]" />
                <span className="text-[10px] text-white/25 uppercase tracking-wider">Enter verification code</span>
                <div className="flex-1 h-px bg-white/[0.06]" />
              </div>

              {/* OTP Input */}
              <div className="mb-3">
                <OtpInput value={otpCode} onChange={setOtpCode} />
              </div>

              {/* Timer */}
              <div className="text-center mb-3">
                {mfaCountdown > 0 ? (
                  <p className="text-white/25 text-xs">
                    Session expires in <span className="text-[#B89B5E] font-mono font-medium">{formatTime(mfaCountdown)}</span>
                  </p>
                ) : (
                  <p className="text-red-400/70 text-xs font-medium">Session expired. Please sign in again.</p>
                )}
              </div>

              <Button
                onClick={handleSetupComplete}
                disabled={mfaLoading || otpCode.length !== 6 || mfaCountdown === 0}
                className="w-full h-11 rounded-xl bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-500 hover:to-emerald-600 text-white font-semibold text-sm shadow-lg shadow-emerald-600/20 transition-all duration-300 border-0 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {mfaLoading ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <>
                    <Shield className="w-4 h-4" />
                    Enable 2FA & Sign In
                  </>
                )}
              </Button>
            </div>
          )}

          {/* ════════════════ STEP 3: OTP VERIFY (RETURNING USERS) ════════════════ */}
          {step === 'mfa_verify' && (
            <div className="animate-in fade-in slide-in-from-right-4 duration-500">
              <button onClick={goBack} className="flex items-center gap-1.5 text-white/40 hover:text-white/70 text-sm mb-6 transition-colors group">
                <ArrowLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />
                Back to sign in
              </button>

              <StepIndicator current={2} total={3} />

              <div className="flex justify-center mb-6">
                <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-[#B89B5E]/20 to-[#B89B5E]/5 border border-[#B89B5E]/20 flex items-center justify-center relative">
                  <Fingerprint className="w-9 h-9 text-[#B89B5E]" />
                  <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-[#B89B5E] flex items-center justify-center">
                    <Shield className="w-3 h-3 text-white" />
                  </div>
                </div>
              </div>

              <div className="text-center mb-8">
                <h2 className="text-2xl font-bold text-white mb-2">Verification Required</h2>
                <p className="text-white/40 text-sm">
                  Enter the 6-digit code from your<br />authenticator app
                </p>
                <p className="text-white/20 text-xs mt-2 font-mono">{mfaEmail}</p>
              </div>

              <form onSubmit={handleMfaVerify} className="space-y-6">
                <OtpInput value={otpCode} onChange={setOtpCode} />

                <div className="text-center">
                  {mfaCountdown > 0 ? (
                    <p className="text-white/25 text-xs">
                      Session expires in <span className="text-[#B89B5E] font-mono font-medium">{formatTime(mfaCountdown)}</span>
                    </p>
                  ) : (
                    <p className="text-red-400/70 text-xs font-medium">Session expired. Please sign in again.</p>
                  )}
                </div>

                <Button
                  type="submit"
                  disabled={mfaLoading || otpCode.length !== 6 || mfaCountdown === 0}
                  className="w-full h-12 rounded-xl bg-gradient-to-r from-[#B89B5E] to-[#9A8249] hover:from-[#C9AA6A] hover:to-[#A8904F] text-white font-semibold text-sm shadow-lg shadow-[#B89B5E]/20 transition-all duration-300 border-0 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {mfaLoading ? (
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <>
                      <Shield className="w-4 h-4" />
                      Verify & Sign In
                    </>
                  )}
                </Button>
              </form>

              <div className="mt-8 p-4 rounded-xl border border-white/[0.04] bg-white/[0.02] text-center">
                <p className="text-white/25 text-xs leading-relaxed">
                  Open your authenticator app (<span className="text-white/50 font-medium">Google Authenticator</span>, <span className="text-white/50 font-medium">Microsoft Authenticator</span>, or <span className="text-white/50 font-medium">Duo Mobile</span>) and enter the 6-digit code shown for <span className="text-white/50 font-medium">Neural News</span>.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0) translateX(0); opacity: 0.08; }
          25% { transform: translateY(-20px) translateX(10px); opacity: 0.12; }
          50% { transform: translateY(-10px) translateX(-5px); opacity: 0.06; }
          75% { transform: translateY(-30px) translateX(15px); opacity: 0.1; }
        }
      `}</style>
    </div>
  );
}
